import { z } from 'zod';
import { SetFimPathsArgs } from './command.js';
/**
 * The block of `ossec.conf` Bartizo owns (knowledge doc section 8.2: "Bartizo
 * writes its watched paths into a clearly marked, generated block").
 *
 * One renderer for both writers: the installer asks `bartizo-agent fim-block` for
 * the first block (P7.1), and the helper's `set_fim_paths` writes every later one
 * (P7.3). The same settings therefore produce the same bytes whoever writes them,
 * which is what lets a writer see "nothing changed" and skip Wazuh's restart.
 */
/**
 * The markers around the block. Everything between them is replaced on each
 * write; everything outside is left as it was.
 *
 * `wazuh-control` greps the whole file for its section tags to decide which
 * daemons to start — a comment naming the auth or cluster tag is enough to make
 * it misread the file and refuse to start (seen with 4.14.8) — so these say
 * nothing of the kind. XML comments also may not contain two hyphens in a row.
 */
export const FIM_BLOCK_BEGIN = "<!-- BARTIZO FIM BEGIN: generated from the watched paths in Bartizo's settings. Edits between these markers are replaced. -->";
export const FIM_BLOCK_END = '<!-- BARTIZO FIM END -->';
/**
 * How a writer finds the block: a line starting with this, so the words after it
 * can change without a writer that no longer finds its own block. The installer
 * has the same string (`WAZUH_BLOCK_BEGIN_PREFIX` in `installer/lib/wazuh.sh`).
 */
export const FIM_BLOCK_BEGIN_PREFIX = '<!-- BARTIZO FIM BEGIN';
/**
 * What `set_fim_paths` did (P7.3). `restarted` matters beyond the paths: a Wazuh
 * restart takes a new baseline without reporting what changed before it, so the
 * agent says so when one happened.
 */
export const FimPathsResult = z.object({
    /** False when `ossec.conf` already held this block: nothing was written or restarted. */
    changed: z.boolean(),
    restarted: z.boolean(),
    notes: z.array(z.string().min(1).max(300)).max(10),
});
/**
 * Section 8.2's default watched paths, by the role that adds them.
 *
 * Differences from the table, each on purpose:
 * - `/bin` and `/sbin` are left out: on all three supported systems they are
 *   symbolic links to `/usr/bin` and `/usr/sbin`, which are watched.
 * - ".bashrc" is read as root's and every user's, and `/etc/profile.d` and
 *   `/etc/bash.bashrc` join `/etc/profile` as the same kind of persistence.
 * - `/etc/sudoers.d` joins `/etc/sudoers`: it is where a sudo rule is dropped today.
 *
 * `whodata` needs the audit daemon; without it Wazuh watches the path in
 * `realtime` instead and says so in its log. Realtime works on a single file
 * as well as a directory (checked on 4.14.8 with `/root/.bashrc`).
 */
export const DEFAULT_FIM_PATHS = {
    base: [
        // Backdoor accounts and password hash changes.
        { path: '/etc/passwd', mode: 'whodata' },
        { path: '/etc/shadow', mode: 'whodata' },
        { path: '/etc/group', mode: 'whodata' },
        { path: '/etc/sudoers', mode: 'whodata' },
        { path: '/etc/sudoers.d', mode: 'whodata' },
        // SSH persistence.
        { path: '/etc/ssh', mode: 'realtime' },
        { path: '/root/.ssh', mode: 'realtime' },
        { path: '/home/*/.ssh', mode: 'realtime' },
        // Scheduled and boot-time persistence.
        { path: '/etc/crontab', mode: 'realtime' },
        { path: '/etc/cron.d', mode: 'realtime' },
        { path: '/var/spool/cron', mode: 'realtime' },
        { path: '/etc/systemd/system', mode: 'realtime' },
        // Dropped exploit files.
        { path: '/tmp', mode: 'realtime' },
        { path: '/var/tmp', mode: 'realtime' },
        { path: '/dev/shm', mode: 'realtime' },
        // Login-time persistence.
        { path: '/root/.bashrc', mode: 'realtime' },
        { path: '/home/*/.bashrc', mode: 'realtime' },
        { path: '/etc/profile', mode: 'realtime' },
        { path: '/etc/profile.d', mode: 'realtime' },
        { path: '/etc/bash.bashrc', mode: 'realtime' },
        // Replaced system tools and boot tampering. In real time since P8.3 (S17):
        // a restart takes a new baseline without reporting what changed since the
        // last scan, so a replaced binary on a scheduled path could be absorbed
        // unreported. Real time sees it when it happens, for one inotify watch per
        // directory; a package upgrade's changes are reported as they land rather
        // than at the next scan, the same events at a different time.
        { path: '/usr/bin', mode: 'realtime' },
        { path: '/usr/sbin', mode: 'realtime' },
        { path: '/usr/local/bin', mode: 'realtime' },
        { path: '/boot', mode: 'realtime' },
        // Log tampering. Scheduled: logs change all the time, and in real time every
        // write would be an event. The one path S17's gap still applies to.
        { path: '/var/log', mode: 'scheduled' },
    ],
    // Webshells. Upload folders differ per application and are added in settings.
    web: [{ path: '/var/www', mode: 'realtime' }],
    database: [],
};
/**
 * Paths under a watched one that are not worth hashing twice a day: the
 * systemd journal is binary, large and rewritten constantly, and
 * `/var/log/bartizo` is Bartizo's own access log (P6.1).
 */
export const DEFAULT_FIM_IGNORE = ['/var/log/bartizo', '/var/log/journal'];
/**
 * What the role defaults alone merge into, in the order the API's merge would put
 * them: watched paths are one entry per path with the later role's mode, sorted by
 * path; ignored paths are a sorted set. `config-merge.test.ts` holds the two
 * together, so the installer's first block is the block the settings would write.
 */
export function fimSettingsForRoles(roles) {
    const ordered = [...new Set(['base', ...roles])].sort();
    const paths = new Map();
    const ignore = new Set();
    for (const role of ordered) {
        for (const entry of DEFAULT_FIM_PATHS[role])
            paths.set(entry.path, { ...entry });
        if (role === 'base')
            for (const path of DEFAULT_FIM_IGNORE)
                ignore.add(path);
    }
    return { paths: byPathOrder(paths), ignore: sortLikeMerge([...ignore]) };
}
/** The entries in the order of their paths, compared as `Array.prototype.sort` does. */
function byPathOrder(entries) {
    return [...entries.entries()]
        .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
        .map(([, entry]) => entry);
}
/** The API's union sorts by each value's canonical JSON; for strings, their quoted form. */
function sortLikeMerge(values) {
    return values
        .map((value) => [JSON.stringify(value), value])
        .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
        .map(([, value]) => value);
}
const MODE_ATTRIBUTES = {
    scheduled: 'check_all="yes"',
    realtime: 'check_all="yes" realtime="yes"',
    whodata: 'check_all="yes" whodata="yes"',
};
/**
 * The generated block, markers included, ending in a newline.
 *
 * Arguments are validated with the helper operation's own schema, and a path that
 * fails it throws rather than being escaped: Wazuh's XML reader is not a general
 * one, and `&amp;` in a path would be watched as those five characters. Entries
 * are deduplicated by path (the last one wins) and sorted, so the same settings
 * always give the same text. `report_changes` is never set: it would put the
 * changed lines of a file such as `/etc/shadow` into the alert, and from there
 * into the central database.
 *
 * The `<global>` element is there for a Wazuh that was installed before Bartizo,
 * whose own configuration may not write `alerts.json`; a second `<global>`
 * section sets only what it names (the package's default file has two).
 */
export function renderFimBlock(settings) {
    const { paths, ignore, scan_interval_seconds: every } = SetFimPathsArgs.parse(settings);
    const byPath = new Map();
    for (const entry of paths)
        byPath.set(entry.path, entry);
    const watched = byPathOrder(byPath);
    const ignored = [...new Set(ignore)].sort();
    const lines = [
        FIM_BLOCK_BEGIN,
        '<ossec_config>',
        '  <global>',
        '    <jsonout_output>yes</jsonout_output>',
        '  </global>',
        '  <syscheck>',
        // A later section's scalar wins (checked on 4.14.8): this overrides the
        // template's twelve hours, and its absence leaves whatever Wazuh had.
        ...(every === undefined ? [] : [`    <frequency>${String(every)}</frequency>`]),
        ...watched.map(({ path, mode }) => `    <directories ${MODE_ATTRIBUTES[mode]}>${path}</directories>`),
        ...ignored.map((path) => `    <ignore>${path}</ignore>`),
        '  </syscheck>',
        '</ossec_config>',
        FIM_BLOCK_END,
    ];
    return `${lines.join('\n')}\n`;
}
/**
 * What Wazuh is asked to watch, from a server's `wazuh_fim` settings: the same
 * whether the helper writes it into a local manager's `ossec.conf` (P7.3) or the
 * API into the server's group on the central manager (P11.8.4).
 */
export const fimPathsArgsOf = (settings) => ({
    paths: settings.paths,
    ignore: settings.ignore,
    ...(settings.scan_interval_seconds === undefined
        ? {}
        : { scan_interval_seconds: settings.scan_interval_seconds }),
});
/**
 * The first line inside a group's `agent.conf` that Bartizo wrote (P11.8.4).
 * No two hyphens in a row, as in every XML comment.
 */
export const AGENT_CONF_MARK = "<!-- Written by Bartizo from this server's watched paths in its settings. Edits here are replaced. -->";
/**
 * A server's group `agent.conf` on the central manager (build plan P11.8.4; W5,
 * W6): the watched paths, and nothing else.
 *
 * Only `<syscheck>` is ever written. A shared `agent.conf` can carry
 * `<localfile><command>`, which runs as root on the agent if its remote commands
 * were on (S23); keeping this file to validated paths is half of what stops
 * that, W6's lock on the agent the other half. Paths are validated with the
 * helper operation's schema and a bad one throws, exactly as `renderFimBlock`:
 * the same entries, in the same order, with the same attributes, so a server
 * moving between local and central mode watches the same things.
 *
 * The agent's own `ossec.conf` has no watched paths in central mode, so this
 * file is the whole list: a path taken out of the settings stops being watched.
 */
export function renderAgentConf(settings) {
    const { paths, ignore, scan_interval_seconds: every } = SetFimPathsArgs.parse(settings);
    const byPath = new Map();
    for (const entry of paths)
        byPath.set(entry.path, entry);
    const watched = byPathOrder(byPath);
    const ignored = [...new Set(ignore)].sort();
    const lines = [
        '<agent_config>',
        `  ${AGENT_CONF_MARK}`,
        '  <syscheck>',
        ...(every === undefined ? [] : [`    <frequency>${String(every)}</frequency>`]),
        ...watched.map(({ path, mode }) => `    <directories ${MODE_ATTRIBUTES[mode]}>${path}</directories>`),
        ...ignored.map((path) => `    <ignore>${path}</ignore>`),
        '  </syscheck>',
        '</agent_config>',
    ];
    return `${lines.join('\n')}\n`;
}
//# sourceMappingURL=wazuh-fim.js.map